/**
 * hm-warehouse-report.ts — zestawienia magazynowe („Magazyn za okres" i pochodne).
 *
 * Osobny kontrakt od sprzedaży, bo magazyn liczy się z innych rzeczy: nie ma VAT-u ani brutto, za
 * to liczy się ILOŚĆ — przy podsumowaniu wg towaru pytanie brzmi „ile sztuk zeszło", nie „za ile".
 * Wspólna z raportem sprzedaży jest arytmetyka i budowa koszyków (`hm-report-core`).
 *
 * PUŁAPKA KONTRAKTU, odwrotna niż na sprzedaży (sprawdzone na żywo 2026-09-13): tutaj `Buffer`
 * przychodzi jako FLAGA, a `Canceled` jako LICZBA — na sprzedaży jest dokładnie na odwrót.
 * Mapper tłumaczy oba kształty, ale nie zakładaj jednego przy dokładaniu pól.
 */

import {
  bucketById,
  bucketByText,
  compareBuckets,
  compareCurrency,
  fromMilli,
  fromMinor,
  HM_REPORT_NONE,
  type HmReportBucketIdentity,
  toMilli,
  toMinor,
} from './hm-report-core';

/** Wymiary podsumowania zestawienia magazynowego. */
export const HM_WAREHOUSE_REPORT_DIMENSIONS = [
  'none',
  'contractor',
  'warehouse',
  'receivingWarehouse',
  'typeCode',
  'series',
  'description',
  'status',
  'settlement',
  'issueDate',
  'issueMonth',
  'operationDate',
  'product',
] as const;

export type HmWarehouseReportDimension = (typeof HM_WAREHOUSE_REPORT_DIMENSIONS)[number];

/**
 * Podsumowanie wg TOWARU wymaga pozycji, a te są tylko w szczegółach dokumentu — Symfonia nie ma
 * pobrania hurtem, więc to prawdziwe N+1 za jawnym przełącznikiem.
 */
export const HM_WAREHOUSE_REPORT_DETAIL_DIMENSIONS = ['product'] as const;

export function hmWarehouseDimensionNeedsDetails(dim: HmWarehouseReportDimension): boolean {
  return (HM_WAREHOUSE_REPORT_DETAIL_DIMENSIONS as readonly string[]).includes(dim);
}

export type HmWarehouseReportGrain = 'document' | 'position';

export interface HmWarehouseReportRow {
  documentId: number;
  documentNumber: string;
  issueDate: string | null;
  operationDate: string | null;
  typeCode: string;
  series: string;
  contractorName: string | null;
  contractorId: number | null;
  warehouseName: string | null;
  receivingWarehouseName: string | null;
  description: string;
  currency: string;
  inBuffer: boolean;
  canceled: boolean;
  /** `false` = dokument nierozliczony (np. WZ bez faktury) — osobny raport z menu. */
  settled: boolean;
  netValue: string;
  documentValue: string;
  /** Wypełnione WYŁĄCZNIE dla ziarna pozycji. */
  positionNo: number | null;
  productCode: string | null;
  productName: string | null;
  unit: string | null;
  quantity: string | null;
}

/**
 * Kwoty jednej waluty plus ILOŚĆ. Ilość jest sumowana tylko przy ziarnie pozycji — sumowanie
 * „sztuk" z nagłówków dokumentów nie znaczyłoby nic.
 */
export interface HmWarehouseMoney {
  currency: string;
  net: string;
  value: string;
  quantity: string;
}

export interface HmWarehouseReportBucket {
  key: string;
  label: string | null;
  labelKey: string | null;
  count: number;
  documentCount: number;
  totals: HmWarehouseMoney[];
  rows: HmWarehouseReportRow[];
}

export interface HmWarehouseReportAggregate {
  rows: HmWarehouseReportRow[];
  buckets: HmWarehouseReportBucket[] | null;
  grandTotals: HmWarehouseMoney[];
  documentCount: number;
  positionCount: number | null;
}

export interface HmWarehouseReportParams {
  dateFrom: string | null;
  dateTo: string | null;
  contractorId: number | null;
  typeCode: string | null;
  series: string | null;
  warehouseName: string | null;
  status: 'buffer' | 'issued' | null;
  /** `unsettled` = tylko nierozliczone; `settled` = tylko rozliczone. */
  settlement: 'settled' | 'unsettled' | null;
  dimension: HmWarehouseReportDimension;
  grain: HmWarehouseReportGrain;
  details: boolean;
  ids: number[];
}

/**
 * Odpowiedź trasy. `buckets` jest tu ZAWSZE puste — tak samo jak w sprzedaży, koszyki buduje
 * przeglądarka, bo i tak musi je przeliczyć po dociągnięciu pozycji.
 */
export interface HmWarehouseReportResponse extends HmWarehouseReportAggregate {
  dimension: HmWarehouseReportDimension;
  grain: HmWarehouseReportGrain;
  detailsLoaded: boolean;
  detailsSkipped: boolean;
  detailsFailed: number[];
  truncated: boolean;
  fetchedAt: string;
  integrationName: string;
  params: HmWarehouseReportParams;
}

/** Pozycje dokumentu magazynowego dociągane osobno (N+1). */
export interface HmWarehouseReportDetail {
  id: number;
  positions: {
    no: number;
    productCode: string;
    productName: string;
    unit: string | null;
    quantity: string;
    netValue: string;
  }[];
}

export function explodeWarehouseToPositions(
  rows: HmWarehouseReportRow[],
  details: HmWarehouseReportDetail[],
): HmWarehouseReportRow[] {
  const byId = new Map(details.map((d) => [d.id, d]));
  return rows.flatMap((row) => {
    const detail = byId.get(row.documentId);
    if (!detail || detail.positions.length === 0) return [];
    return detail.positions.map((p) => ({
      ...row,
      positionNo: p.no,
      productCode: p.productCode,
      productName: p.productName,
      unit: p.unit,
      quantity: p.quantity,
      netValue: p.netValue,
      // Wartość dokumentu jest wielkością NAGŁÓWKA — na pozycji powielona zawyżyłaby sumę tyle
      // razy, ile pozycji ma dokument.
      documentValue: '0.00',
    }));
  });
}

export function sumWarehouseByCurrency(rows: HmWarehouseReportRow[]): HmWarehouseMoney[] {
  const acc = new Map<string, { net: number; value: number; quantity: number }>();
  for (const row of rows) {
    const currency = row.currency || 'PLN';
    const entry = acc.get(currency) ?? { net: 0, value: 0, quantity: 0 };
    entry.net += toMinor(row.netValue);
    entry.value += toMinor(row.documentValue);
    entry.quantity += toMilli(row.quantity);
    acc.set(currency, entry);
  }
  return Array.from(acc.entries())
    .sort(([a], [b]) => compareCurrency(a, b))
    .map(([currency, e]) => ({
      currency,
      net: fromMinor(e.net),
      value: fromMinor(e.value),
      quantity: fromMilli(e.quantity),
    }));
}

export function warehouseBucketIdentityOf(
  row: HmWarehouseReportRow,
  dimension: HmWarehouseReportDimension,
): HmReportBucketIdentity {
  switch (dimension) {
    case 'contractor':
      return bucketById(row.contractorId, row.contractorName, 'c');
    case 'warehouse':
      return bucketByText(row.warehouseName);
    case 'receivingWarehouse':
      return bucketByText(row.receivingWarehouseName);
    case 'typeCode':
      return bucketByText(row.typeCode);
    case 'series':
      return bucketByText(row.series);
    case 'description':
      return bucketByText(row.description);
    case 'status':
      return row.inBuffer
        ? { key: 'buffer', label: null, labelKey: 'buffer' }
        : { key: 'issued', label: null, labelKey: 'issued' };
    case 'settlement':
      return row.settled
        ? { key: 'settled', label: null, labelKey: 'settled' }
        : { key: 'unsettled', label: null, labelKey: 'unsettled' };
    case 'issueDate':
      return bucketByText(row.issueDate?.slice(0, 10) ?? '');
    case 'issueMonth':
      return bucketByText(row.issueDate?.slice(0, 7) ?? '');
    case 'operationDate':
      return bucketByText(row.operationDate?.slice(0, 10) ?? '');
    case 'product':
      return bucketByText(row.productCode, row.productName || row.productCode);
    case 'none':
      return HM_REPORT_NONE;
  }
}

const DATE_DIMENSIONS: readonly HmWarehouseReportDimension[] = [
  'issueDate',
  'issueMonth',
  'operationDate',
];

export function aggregateWarehouseReport(input: {
  rows: HmWarehouseReportRow[];
  dimension: HmWarehouseReportDimension;
  grain: HmWarehouseReportGrain;
}): HmWarehouseReportAggregate {
  const { rows, dimension, grain } = input;

  const documentCount = new Set(rows.map((r) => r.documentId)).size;
  const positionCount = grain === 'position' ? rows.length : null;
  const grandTotals = sumWarehouseByCurrency(rows);

  if (dimension === 'none') {
    return { rows, buckets: null, grandTotals, documentCount, positionCount };
  }

  const acc = new Map<string, { identity: HmReportBucketIdentity; rows: HmWarehouseReportRow[] }>();
  for (const row of rows) {
    const identity = warehouseBucketIdentityOf(row, dimension);
    const entry = acc.get(identity.key) ?? { identity, rows: [] };
    entry.rows.push(row);
    acc.set(identity.key, entry);
  }

  const isDate = DATE_DIMENSIONS.includes(dimension);
  const buckets: HmWarehouseReportBucket[] = Array.from(acc.values())
    .map(({ identity, rows: bucketRows }) => ({
      key: identity.key,
      label: identity.label,
      labelKey: identity.labelKey,
      count: bucketRows.length,
      documentCount: new Set(bucketRows.map((r) => r.documentId)).size,
      totals: sumWarehouseByCurrency(bucketRows),
      rows: bucketRows,
    }))
    .sort((a, b) => compareBuckets(a, b, isDate));

  return { rows: [], buckets, grandTotals, documentCount, positionCount };
}
